import { isAllCategoryTranslation, normalizeAllCategory, getAllTranslation } from "./lang";
import { fetchData } from "@repo/shared-utils/fetch-utils";
import type { LiveStream } from "../types/media-stream.types";

export interface TabItem {
  tabName: string;
  playlistID: string;
  [key: string]: unknown;
}

/**
 * Normalizes category labels with language-aware "All" translation handling.
 */
export function normalizeCategoryName(categoryName: string, currentLanguage?: string): string {
  if (!categoryName || typeof categoryName !== "string") {
    return categoryName;
  }

  return normalizeAllCategory(categoryName, currentLanguage);
}

/** Capitalize first letter of a string for display (e.g. category tab labels). */
export function capitalizeFirstLetter(str: string): string {
  if (!str || typeof str !== "string") return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Returns true when the supplied label maps to the localized "All" category.
 */
export function isAllCategory(categoryName: string, currentLanguage?: string): boolean {
  return isAllCategoryTranslation(categoryName, currentLanguage);
}

export interface MediaData {
  live_streams: LiveStream[];
}

export interface TabPaginationMeta {
  page: number;
  limit: number;
  total: number;
}

/** Shape of the raw video stream returned from backend APIs. */
export type VideoStream = MediaData["live_streams"][number];

/** Supported shapes returned by backend playlist/video APIs. */
export interface PlaylistApiResponse {
  data?: {
    upcomingliveFromFireworks?: { live_streams?: VideoStream[] };
    videos?: VideoStream[];
    page?: number;
    limit?: number;
    total?: number;
  };
  upcomingliveFromFireworks?: { live_streams?: VideoStream[] };
  videos?: VideoStream[];
  live_streams?: VideoStream[];
  page?: number;
  limit?: number;
  total?: number;
}

/**
 * Extracts stream arrays from the supported backend response shapes.
 * @param apiResponse - Raw response object from playlist/video API
 * @returns Array of video stream objects
 */
export function getMediaStreamsFromApiResponse(
  apiResponse: PlaylistApiResponse | null | undefined
): VideoStream[] {
  return (
    apiResponse?.data?.upcomingliveFromFireworks?.live_streams ??
    apiResponse?.data?.videos ??
    apiResponse?.upcomingliveFromFireworks?.live_streams ??
    apiResponse?.videos ??
    apiResponse?.live_streams ??
    []
  );
}

/**
 * Normalizes playlist API response payload into the local MediaData shape.
 * @param apiResponse - Raw response object from playlist/video API
 * @returns Normalized MediaData with live_streams array
 */
export function extractMediaDataFromApiResponse(
  apiResponse: PlaylistApiResponse | null | undefined
): MediaData {
  const liveStreams = getMediaStreamsFromApiResponse(apiResponse);
  return {
    live_streams: Array.isArray(liveStreams) ? liveStreams : [],
  };
}

/**
 * Extracts pagination metadata from multiple API response envelopes.
 * @param apiResponse - Raw response object from playlist/video API
 * @param fallback - Default page and limit values if not present in response
 * @returns TabPaginationMeta with page, limit, and total
 */
export function extractPaginationFromApiResponse(
  apiResponse: PlaylistApiResponse | null | undefined,
  fallback: { page: number; limit: number }
): TabPaginationMeta {
  const pageRaw = apiResponse?.data?.page ?? apiResponse?.page;
  const limitRaw = apiResponse?.data?.limit ?? apiResponse?.limit;
  const totalRaw = apiResponse?.data?.total ?? apiResponse?.total;

  const page = Number(pageRaw);
  const limit = Number(limitRaw);
  const total = Number(totalRaw);

  return {
    page: Number.isFinite(page) && page > 0 ? page : fallback.page,
    limit: Number.isFinite(limit) && limit > 0 ? limit : fallback.limit,
    total: Number.isFinite(total) && total >= 0 ? total : 0,
  };
}

/**
 * Fetches one paginated playlist dataset and maps it into media + pagination metadata.
 */
export async function fetchPaginatedPlaylistMedia(options: {
  endpointBase: string;
  playlistId: string;
  pagination: { page: number; limit: number };
  videoDescription?: string;
  logContext?: string;
}): Promise<{ mediaData: MediaData; meta: TabPaginationMeta }> {
  const {
    endpointBase,
    playlistId,
    pagination,
    videoDescription,
    logContext = "PlaylistAPI",
  } = options;
  const videoDescriptionQuery = videoDescription
    ? `&video_description=${encodeURIComponent(videoDescription)}`
    : "";
  const endpoint =
    `${endpointBase}` +
    `?playlistId=${encodeURIComponent(playlistId)}` +
    videoDescriptionQuery +
    `&page=${pagination.page}&limit=${pagination.limit}`;

  try {
    const response = await fetch(endpoint, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      return {
        mediaData: { live_streams: [] },
        meta: { page: pagination.page, limit: pagination.limit, total: 0 },
      };
    }

    const apiResponseJson = (await response.json()) as PlaylistApiResponse;
    const mediaData = extractMediaDataFromApiResponse(apiResponseJson);
    const meta = extractPaginationFromApiResponse(apiResponseJson, pagination);
    return { mediaData, meta };
  } catch (error) {
    console.error(`[${logContext}] request-failed`, {
      playlistId,
      endpoint,
      error,
    });

    return {
      mediaData: { live_streams: [] },
      meta: { page: pagination.page, limit: pagination.limit, total: 0 },
    };
  }
}

/**
 * Extracts the user-facing category label from a video payload.
 * @param videoStream - A single video stream object from MediaData
 * @returns Trimmed event description or category, or empty string if neither exists
 */
function getVideoEventDescription(videoStream: VideoStream): string {
  const eventDescription = videoStream?.event_description;
  if (typeof eventDescription === "string" && eventDescription.trim()) {
    return eventDescription.trim();
  }

  const category = videoStream?.category;
  if (typeof category === "string" && category.trim()) {
    return category.trim();
  }

  return "";
}

/**
 * Resolves a stable dedupe key for a video stream from the common id fields.
 */
function resolveVideoStreamKey(stream: VideoStream): string {
  return String(stream.encoded_id || stream.id || stream.trailer?.encoded_id || "");
}

/**
 * Creates a temporary stream key when encoded/id fields are missing.
 */
function createTempStreamKey(): string {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? `temp-${crypto.randomUUID()}`
    : `temp-${Date.now()}`;
}

/**
 * Deduplicates video streams by id and merges hashtags, filtering out "All" tags.
 */
function mergeStreamsByVideoId(streams: VideoStream[]): VideoStream[] {
  const videoMap = new Map<string, VideoStream>();

  streams.forEach((stream) => {
    const videoId = resolveVideoStreamKey(stream);
    const streamHashtags = stream.trailer?.hashtags || [];

    if (videoId && videoMap.has(videoId)) {
      const existingVideo = videoMap.get(videoId);
      const existingHashtags = existingVideo?.trailer?.hashtags || [];
      const mergedHashtags = Array.from(new Set([...existingHashtags, ...streamHashtags])).filter(
        (tag: string) => !isAllCategory(tag)
      );
      videoMap.set(videoId, {
        ...existingVideo,
        trailer: {
          ...existingVideo?.trailer,
          hashtags: mergedHashtags,
        },
      });
      return;
    }

    const finalHashtags = streamHashtags.filter((tag: string) => !isAllCategory(tag));
    videoMap.set(videoId || createTempStreamKey(), {
      ...stream,
      trailer: {
        ...stream.trailer,
        hashtags: finalHashtags,
      },
    });
  });

  return Array.from(videoMap.values()).map((video) => ({
    ...video,
    trailer: {
      ...video.trailer,
      hashtags: video.trailer?.hashtags || [],
    },
  }));
}

/**
 * Fetches media data for a single playlistId.
 * This function is used when we have a single playListId at the section level (e.g., shopOnEmi.playListId).
 * Returns videos with their original hashtags from API.
 */
export async function fetchMediaDataForPlaylist(
  playListId: string | undefined,
  tabs: Array<Record<string, TabItem>> | undefined,
  getPlaylistEndpoint: (playlistId: string) => string
): Promise<MediaData> {
  if (!playListId) {
    return { live_streams: [] };
  }

  try {
    const json = await fetchData({
      endpoint: getPlaylistEndpoint(playListId),
      cache: "no-store",
      headers: { Accept: "application/json" },
    });

    if (!json) {
      throw new Error(`Failed to fetch playlist: No data returned`);
    }
    const streams = getMediaStreamsFromApiResponse(json);

    // Process streams: use only hashtags from API response, no grouping
    const processedStreams = streams.map((s) => {
      const existingHashtags = s.trailer?.hashtags || [];

      // Keep only original hashtags from API, filter out "All" category
      const allHashtags = existingHashtags.filter(
        (tag: string) => tag && typeof tag === "string" && tag.trim() && !isAllCategory(tag)
      );

      return {
        ...s,
        category: s.event_description,
        trailer: {
          ...s.trailer,
          hashtags: allHashtags,
        },
      };
    });

    const mediaData = { live_streams: mergeStreamsByVideoId(processedStreams) };

    // Replace hashtags with matching tabNames if tabs are provided
    return replaceHashtagsWithTabNames(mediaData, tabs);
  } catch {
    return { live_streams: [] };
  }
}

/**
 * Fetches and merges media for multiple tab playlist IDs into one normalized dataset.
 */
export async function fetchAllMediaData(
  data: Record<string, TabItem> | TabItem[] | null,
  getPlaylistEndpoint: (playlistId: string) => string
): Promise<MediaData> {
  if (!data) {
    return { live_streams: [] };
  }

  const categoryItems = Array.isArray(data) ? data : Object.values(data);

  try {
    const playlistPromises = categoryItems.map(async (item: TabItem) => {
      try {
        const json = await fetchData({
          endpoint: getPlaylistEndpoint(item.playlistID),
          cache: "no-store",
          headers: { Accept: "application/json" },
        });

        if (!json) {
          throw new Error(`Failed to fetch playlist: No data returned`);
        }
        const streams = getMediaStreamsFromApiResponse(json);

        return streams.map((s) => {
          // Use only hashtags from API response - no tabName injection
          const existingHashtags = s.trailer?.hashtags || [];

          return {
            ...s,
            category: s.event_description,
            trailer: {
              ...s.trailer,
              hashtags: existingHashtags,
            },
          };
        });
      } catch {
        return [];
      }
    });

    const allStreamsArrays = await Promise.all(playlistPromises);
    const allStreams = allStreamsArrays.flat();
    const mediaData = { live_streams: mergeStreamsByVideoId(allStreams) };

    // Convert data to tabs format for hashtag replacement
    // tabs format is Array<Record<string, TabItem>>
    let tabs: Array<Record<string, TabItem>> | undefined;
    if (data) {
      if (Array.isArray(data)) {
        // If data is an array, wrap each item in a record
        tabs = data.map((item) => ({ [item.tabName]: item }));
      } else {
        // If data is a Record<string, TabItem>, wrap it in an array
        tabs = [data];
      }
    }

    // Replace hashtags with matching tabNames if tabs are available
    return replaceHashtagsWithTabNames(mediaData, tabs);
  } catch {
    return { live_streams: [] };
  }
}

/**
 * Derive category tabs from hashtags present in the fetched media.
 * Returns ["All" (translated), ...unique hashtags] for use as category tabs.
 * Use this when categories should be driven by hashtags in the response, not by config tab names.
 */
export function getCategoriesFromHashtags(
  mediaData?: MediaData | null,
  language?: string
): string[] {
  if (!mediaData?.live_streams || mediaData.live_streams.length === 0) {
    return [];
  }

  const allHashtags = new Set<string>();
  mediaData.live_streams.forEach((video) => {
    const hashtags = video.trailer?.hashtags || [];
    hashtags.forEach((tag: string) => {
      if (tag && typeof tag === "string" && tag.trim() && !isAllCategory(tag)) {
        allHashtags.add(tag.trim());
      }
    });
  });

  if (allHashtags.size === 0) return [];

  const allLabel = getAllTranslation(language || "en");
  const sortedHashtags = Array.from(allHashtags).sort((a, b) => a.localeCompare(b));
  return [allLabel, ...sortedHashtags];
}

/**
 * Normalize a string for comparison: trim, lowercase, and remove spaces
 */
function normalizeForComparison(str: string): string {
  if (typeof str !== "string") return "";
  return str.trim().toLowerCase().replaceAll(/\s+/g, "");
}

/**
 * Replace hashtags with matching tabNames (category values).
 * If a hashtag matches a tabName (normalized comparison), replace the hashtag with the tabName.
 * Example: hashtag "goldloan" matches tabName "Gold Loan", so replace "goldloan" with "Gold Loan"
 */
function replaceHashtagsWithTabNames(
  mediaData: MediaData,
  tabs?: Array<Record<string, TabItem>>
): MediaData {
  if (!tabs || !Array.isArray(tabs) || tabs.length === 0) {
    return mediaData;
  }

  // Create a mapping from normalized hashtags to tabNames
  const normalizedToTabName = new Map<string, string>();
  tabs.forEach((tabRecord) => {
    Object.values(tabRecord).forEach((tabItem: TabItem) => {
      if (tabItem?.tabName && typeof tabItem.tabName === "string") {
        const normalizedTabName = normalizeForComparison(tabItem.tabName);
        normalizedToTabName.set(normalizedTabName, tabItem.tabName.trim());
      }
    });
  });

  if (normalizedToTabName.size === 0) {
    return mediaData;
  }

  // Process each video and replace matching hashtags with tabNames
  const processedStreams = mediaData.live_streams.map((video) => {
    const hashtags = video.trailer?.hashtags || [];
    const replacedHashtags = hashtags.map((tag: string) => {
      if (!tag || typeof tag !== "string") return tag;

      const normalizedTag = normalizeForComparison(tag);
      // If this normalized hashtag matches a tabName, replace it with the tabName
      if (normalizedToTabName.has(normalizedTag)) {
        return normalizedToTabName.get(normalizedTag)!;
      }
      return tag;
    });

    return {
      ...video,
      trailer: {
        ...video.trailer,
        hashtags: replacedHashtags,
      },
    };
  });

  return { live_streams: processedStreams };
}

/**
 * Extract tabNames from tabs array and create categories list with "ALL" option.
 * Returns ["All" (translated), ...tabNames] for use as category tabs.
 * @param tabs - Array of tab configuration records
 * @param language - Language code for localized "All" label
 * @returns Array of category names starting with localized "All"
 */
export function getCategoriesFromTabs(
  tabs?: Array<Record<string, TabItem>>,
  language?: string
): string[] {
  if (!tabs || !Array.isArray(tabs) || tabs.length === 0) {
    return [];
  }

  const allLabel = getAllTranslation(language || "en");
  // Use Map to dedupe by normalized form while preserving first-seen insertion order
  const normalizedToOriginal = new Map<string, string>();

  tabs.forEach((tabRecord) => {
    Object.values(tabRecord).forEach((tabItem: TabItem) => {
      const value = tabItem?.tabName;
      if (typeof value !== "string") return;

      const trimmed = value.trim();
      if (!trimmed || isAllCategory(trimmed, language)) return;

      const key = normalizeForComparison(trimmed);
      if (!normalizedToOriginal.has(key)) {
        normalizedToOriginal.set(key, trimmed);
      }
    });
  });

  if (normalizedToOriginal.size === 0) return [];

  return [allLabel, ...Array.from(normalizedToOriginal.values())];
}

/**
 * Determines whether a playlist section should render based on configured tabs and media readiness.
 * @param tabData - Tab configuration as Record or TabItem array
 * @param mediaData - Loaded media data (optional during loading state)
 * @param language - Language code for localized category checks
 * @returns true if section should be rendered
 */
export function shouldShowPlaylistSection(
  tabData?: Record<string, TabItem> | TabItem[] | null,
  mediaData?: MediaData | null,
  language?: string
): boolean {
  if (!tabData) return false;

  if (Array.isArray(tabData) && tabData.length === 0) return false;

  if (!Array.isArray(tabData) && typeof tabData === "object" && Object.keys(tabData).length === 0)
    return false;

  // If mediaData is loaded, check if we have categories from AEM tabs.
  if (mediaData !== undefined && mediaData !== null) {
    const normalizedTabs = Array.isArray(tabData)
      ? tabData.map((item) => ({ [item.tabName]: item }))
      : [tabData];
    const categoriesFromTabs = getCategoriesFromTabs(normalizedTabs, language);
    return categoriesFromTabs.length > 0;
  }

  // While loading, show section if we have tab data to fetch
  return true;
}

/**
 * Check if a playlist section should be shown based on playListId and mediaData.
 * Used for sections with playListId at the section level.
 * If tabs are provided, checks if videos match any of the tabs.
 */
export function shouldShowPlaylistSectionWithId(
  playListId?: string,
  mediaData?: MediaData | null,
  language?: string,
  tabs?: Array<Record<string, TabItem>>
): boolean {
  if (!playListId) return false;

  // If mediaData is loaded, check if we have categories
  if (mediaData !== undefined && mediaData !== null) {
    // If tabs are provided, use getCategoriesFromTabs to check if videos match tabs
    if (tabs && Array.isArray(tabs) && tabs.length > 0) {
      const categoriesFromTabs = getCategoriesFromTabs(tabs, language);
      return categoriesFromTabs.length > 0;
    }

    // Otherwise, check if we have categories from hashtags
    const categoriesFromHashtags = getCategoriesFromHashtags(mediaData, language);
    return categoriesFromHashtags.length > 0;
  }

  // While loading, show section if we have playListId to fetch
  return true;
}

/**
 * Filters videos by active category using normalized event description matching.
 */
export function filterVideosByCategory(
  videos: MediaData["live_streams"],
  activeCategory: string,
  language?: string,
  availableCategories?: string[]
): MediaData["live_streams"] {
  if (!videos || videos.length === 0) return [];

  const videosWithEventDescription = videos.filter((video) => !!getVideoEventDescription(video));

  // If availableCategories are provided, filter videos to only include those
  // whose event description matches at least one available category (excluding "All")
  if (availableCategories && availableCategories.length > 0) {
    // Get normalized category names (excluding "All")
    const normalizedCategories = new Set<string>();
    availableCategories.forEach((cat) => {
      if (!isAllCategory(cat, language)) {
        normalizedCategories.add(normalizeForComparison(cat));
      }
    });

    // Only keep videos whose event description matches at least one available category
    const videosMatchingCategories = videosWithEventDescription.filter((video) => {
      const normalizedEventDescription = normalizeForComparison(getVideoEventDescription(video));
      return normalizedCategories.has(normalizedEventDescription);
    });

    // If no categories match, return empty array
    if (normalizedCategories.size === 0) {
      return [];
    }

    // If "All" is selected, return all videos that match any category
    if (isAllCategory(activeCategory, language)) {
      return videosMatchingCategories;
    }

    // Otherwise, filter by the active category
    const normalizedActive = normalizeForComparison(activeCategory);
    return videosMatchingCategories.filter(
      (video) => normalizeForComparison(getVideoEventDescription(video)) === normalizedActive
    );
  }

  // Fallback to original behavior when no availableCategories provided
  if (isAllCategory(activeCategory, language)) {
    return videosWithEventDescription;
  }

  // Compare event descriptions with activeCategory by normalizing:
  // - trim spaces
  // - convert to lowercase
  // - remove internal spaces so matching remains resilient to formatting
  const normalizedActive = normalizeForComparison(activeCategory);

  return videosWithEventDescription.filter(
    (video) => normalizeForComparison(getVideoEventDescription(video)) === normalizedActive
  );
}

/**
 * Returns true if mediaData has at least one video whose event description matches any of
 * the available categories (excluding "All"). Used to decide whether to show the playlist section.
 */
export function hasVideosMatchingCategories(
  mediaData: MediaData | null,
  availableCategories: string[],
  language?: string
): boolean {
  if (!mediaData?.live_streams || mediaData.live_streams.length === 0) {
    return false;
  }
  if (!availableCategories || availableCategories.length === 0) {
    return false;
  }

  const normalizedCategories = new Set<string>();
  availableCategories.forEach((cat) => {
    if (!isAllCategory(cat, language)) {
      const normalized = normalizeCategoryName(cat, language);
      normalizedCategories.add(normalizeForComparison(normalized));
    }
  });

  if (normalizedCategories.size === 0) {
    return false;
  }

  return mediaData.live_streams.some((video) => {
    const eventDescription = getVideoEventDescription(video);
    if (!eventDescription) return false;
    const normalizedEventDescription = normalizeForComparison(eventDescription);
    return normalizedCategories.has(normalizedEventDescription);
  });
}
